/* eslint-disable @typescript-eslint/no-explicit-any */
/* ────────────────────────────────────────────────────────────────────────
   salaryStructure.schema.ts

   schemaFor(type) returns a yup schema containing ONLY that type's fields,
   sharing one basic-information base (Structure Name, Description, the
   persisted structureType). Validation ranges/messages are preserved from the
   existing add/update forms. The Noida-PT rule (cannot be enabled while
   Professional Tax is applicable) is kept exactly.
   ──────────────────────────────────────────────────────────────────────── */
import * as yup from "yup";
import { validateNoLeadingTrailingWhitespace } from "util/validation";
import { StructureType, TemplateId, templateForType } from "./salaryStructure.config";

// Preserve the existing transform so a cleared number field reports "required"
// rather than "must be a number".
export const toNumberOrUndefined = (value: unknown, originalValue: unknown) => {
  if (originalValue === "" || originalValue === null || originalValue === undefined) {
    return undefined;
  }
  return Number.isNaN(value) ? undefined : value;
};

const basicShape = {
  structureType: yup
    .mixed<StructureType>()
    .oneOf(["PERMANENT_EMPLOYEE", "CONTRACT_EMPLOYEE", "TRAINEE", "INTERN", "CONSULTANT"])
    .required(),
  structureName: yup
    .string()
    .required("Structure name is required")
    .test("not-only-spaces", "Structure name cannot be empty spaces", validateNoLeadingTrailingWhitespace),
  description: yup.string().optional(),
};

/* ── EMPLOYEE — preserves the legacy ranges/messages ─────────────────────── */
const employeeShape = {
  ...basicShape,
  basicSalaryPercentage: yup
    .number()
    .transform(toNumberOrUndefined)
    .required("Basic Salary % is required")
    .min(30, "Basic Salary Percentage must be greater than or equal to 30")
    .max(60, "Basic Salary Percentage cannot be greater than 60")
    .test("max-decimals", "Only 2 decimal places allowed", (value) =>
      value == null ? true : /^\d+(\.\d{1,2})?$/.test(value.toString())
    ),
  hraPercentage: yup
    .number()
    .transform(toNumberOrUndefined)
    .required("HRA % is required")
    .min(10, "HRA Percentage must be greater than or equal to 10")
    .max(50, "HRA Percentage can not be greater than 50"),
  halfYearBonusPercentage: yup.number().transform(toNumberOrUndefined).min(0).max(25).nullable(),
  performanceBonusPercentage: yup.number().transform(toNumberOrUndefined).min(0).max(25).nullable(),
  annualBonusPercentage: yup
    .number()
    .transform(toNumberOrUndefined)
    .min(0)
    .nullable()
    .test("annual-bonus-max", "Annual Bonus must be ≤ 20 in percentage mode", function (value) {
      if (this.parent.annualBonusIsPercentage !== false && value != null && value > 20) return false;
      return true;
    }),
  annualBonusIsPercentage: yup.boolean().nullable().default(true),
  conveyanceAllowance: yup.number().transform(toNumberOrUndefined).min(0).nullable(),
  conveyanceIsPercentage: yup.boolean().nullable().default(false),
  medicalAllowance: yup.number().transform(toNumberOrUndefined).min(0).nullable(),
  medicalAllowanceIsPercentage: yup.boolean().nullable().default(false),
  leaDays: yup.number().transform(toNumberOrUndefined).min(1).max(365).integer().nullable(),
  leaMonthly: yup.number().transform(toNumberOrUndefined).min(0).nullable(),
  leaIsPercentage: yup.boolean().nullable().default(false),
  medicalInsuranceApplicable: yup.boolean().nullable().default(false),
  medicalInsuranceMonthly: yup.number().transform(toNumberOrUndefined).min(0).nullable(),
  medicalInsuranceIsPercentage: yup.boolean().nullable().default(false),
  pfApplicable: yup.boolean().nullable().default(false),
  pfWageCeilingApplicable: yup.boolean().nullable().default(false),
  providentFundEmployeePercentage: yup.number().transform(toNumberOrUndefined).min(0).max(12).nullable(),
  providentFundEmployerPercentage: yup.number().transform(toNumberOrUndefined).min(0).max(12).nullable(),
  esiApplicable: yup.boolean().nullable().default(false),
  esiEmployerPercentage: yup.number().transform(toNumberOrUndefined).min(0).nullable(),
  esiEmployeePercentage: yup.number().transform(toNumberOrUndefined).min(0).nullable(),
  profTaxApplicable: yup.boolean().nullable().default(false),
  taxOnProfession: yup.number().transform(toNumberOrUndefined).min(0).max(2500).nullable(),
  // Existing business rule kept verbatim: Noida PT cannot coexist with Prof Tax.
  noidaPtApplicable: yup
    .boolean()
    .nullable()
    .default(false)
    .test(
      "noida-pt-without-prof-tax",
      "Noida PT cannot be applicable when Professional Tax is applicable",
      function (value) {
        if (this.parent.profTaxApplicable && value) return false;
        return true;
      }
    ),
};

/* ── CONTRACT (Contract Employee / Consultant) ───────────────────────────── */
const contractShape = {
  ...basicShape,
  contractType: yup.mixed<"GST" | "NON_GST">().oneOf(["GST", "NON_GST"]).required(),
  contractValue: yup
    .number()
    .transform(toNumberOrUndefined)
    .required("Contract value is required")
    .min(0, "Cannot be negative"),
  monthlyPayout: yup
    .number()
    .transform(toNumberOrUndefined)
    .required("Monthly payout is required")
    .min(0, "Cannot be negative"),
  tdsRate: yup.number().transform(toNumberOrUndefined).min(0).max(100).nullable(),
  // GST % is only relevant / required for GST contracts.
  gstPercentage: yup
    .number()
    .transform(toNumberOrUndefined)
    .min(0)
    .max(28)
    .nullable()
    .when("contractType", {
      is: "GST",
      then: (s) => s.required("GST % is required for a GST contract"),
    }),
};

/* ── TRAINEE / INTERN ────────────────────────────────────────────────────── */
const traineeShape = {
  ...basicShape,
  monthlyStipend: yup
    .number()
    .transform(toNumberOrUndefined)
    .required("Monthly stipend is required")
    .min(0, "Cannot be negative"),
  periodMonths: yup
    .number()
    .transform(toNumberOrUndefined)
    .required("Training period is required")
    .min(1, "At least 1 month")
    .max(60, "At most 60 months")
    .integer("Whole months only"),
  pfApplicable: yup.boolean().nullable().default(false),
  esiApplicable: yup.boolean().nullable().default(false),
  insuranceApplicable: yup.boolean().nullable().default(false),
};

const SCHEMAS_BY_TEMPLATE: Record<TemplateId, yup.AnyObjectSchema> = {
  EMPLOYEE: yup.object().shape(employeeShape),
  CONTRACT: yup.object().shape(contractShape),
  TRAINEE: yup.object().shape(traineeShape),
};

export const schemaFor = (type: StructureType): yup.AnyObjectSchema =>
  SCHEMAS_BY_TEMPLATE[templateForType(type)] ?? SCHEMAS_BY_TEMPLATE.EMPLOYEE;
